import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import * as THREE from "three";
import { WorkshopArt } from "../art/workshop";

/**
 * A row of low-poly crates (Master Spec §21, §22, §45). Each box has a
 * "nearest cursor" indicator (presentation only, resets automatically as
 * the player walks) and a persistent visual state — hidden / checked /
 * found — driven entirely by the Algorithm Engine's domain state, never
 * guessed from mesh geometry (Master Spec §13). Materials and geometry
 * are shared across all boxes (Master Spec §46) — no per-box duplicates.
 */
export type BoxVisualState = "hidden" | "checked" | "found" | "eliminated";

export interface BoxRowHandles {
  group: THREE.Group;
  bounds: { minX: number; maxX: number };
  getNearestIndex: (x: number) => number;
  getBoxWorldX: (index: number) => number;
  setNearestIndex: (index: number | null) => void;
  setBoxState: (index: number, state: BoxVisualState, value?: number) => void;
  update: (dt:number) => void;
  dispose: () => void;
}

const BOX_SIZE = 0.9;
const BOX_COLOR = 0xc58a50;
const NEAREST_COLOR = 0xeeb971;
const CHECKED_COLOR = 0x78ac91;
const FOUND_COLOR = 0x4caf6d;
const ELIMINATED_COLOR = 0x2a2f3d;
const LABEL_TEXT_COLOR = "#1c2340";

function createValueLabel(): { sprite: THREE.Mesh; setValue: (value: number | string) => void; dispose: () => void } {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const context = canvas.getContext("2d");

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace=THREE.SRGBColorSpace;
  const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true });
  const labelGeometry=new THREE.PlaneGeometry(1,1);
  const sprite = new THREE.Mesh(labelGeometry,material);
  sprite.scale.set(0.6, 0.6, 1);
  sprite.visible = true;

  function setValue(value: number | string): void {
    if (!context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "#fff0d6";
    context.fillRect(7, 7, 114, 114);
    context.fillStyle = LABEL_TEXT_COLOR;
    context.font = "bold 64px sans-serif";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(String(value), canvas.width / 2, canvas.height / 2);
    texture.needsUpdate = true;
    sprite.visible = true;
  }

  function dispose(): void {
    labelGeometry.dispose();
    texture.dispose();
    material.dispose();
  }

  return { sprite, setValue, dispose };
}

export function createBoxRow(count: number, spacing: number): BoxRowHandles {
  const group = new THREE.Group();
  const art = new WorkshopArt();
  const geometry = new RoundedBoxGeometry(BOX_SIZE, BOX_SIZE, BOX_SIZE, 2, .035);
  const hiddenMaterial = new THREE.MeshStandardMaterial({ color: BOX_COLOR, roughness: 0.6 });
  const nearestMaterial = new THREE.MeshStandardMaterial({ color: NEAREST_COLOR, roughness: 0.4 });
  const checkedMaterial = new THREE.MeshStandardMaterial({ color: CHECKED_COLOR, roughness: 0.7 });
  const foundMaterial = new THREE.MeshStandardMaterial({ color: FOUND_COLOR, roughness: 0.4 });
  const eliminatedMaterial = new THREE.MeshStandardMaterial({ color: ELIMINATED_COLOR, roughness: 0.9 });

  const meshes: THREE.Mesh[] = [];
  const labels: ReturnType<typeof createValueLabel>[] = [];
  const badges: THREE.Mesh[] = [];
  const states: BoxVisualState[] = new Array(count).fill("hidden");
  const pulses = new Array<number>(count).fill(0);
  const rings:THREE.Mesh[]=[];
  const ringGeometry=new THREE.RingGeometry(.55,.59,40);
  const ringMaterial=new THREE.MeshBasicMaterial({color:0x7fffa0,transparent:true,opacity:.8,side:THREE.DoubleSide,depthWrite:false});
  let elapsed=0;
  let nearestIndex: number | null = null;

  const startX = -((count - 1) * spacing) / 2;

  for (let i = 0; i < count; i += 1) {
    const mesh = new THREE.Mesh(geometry, hiddenMaterial);
    mesh.position.set(startX + i * spacing, BOX_SIZE / 2, 0);
    mesh.name="parcel";
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
    meshes.push(mesh);
    const ring=new THREE.Mesh(ringGeometry,ringMaterial);ring.rotation.x=-Math.PI/2;ring.position.set(mesh.position.x,.03,0);ring.visible=false;group.add(ring);rings.push(ring);
    art.box(mesh,0xad733f,[.025,.89,.015],[.1,0,.455]);
    for(let line=0;line<5;line++)art.box(mesh,0x775337,[.015,.1,.012],[.23+line*.026,-.2,.46]);

    art.box(mesh, 0xe9bd86, [.16,.022,.91], [0,.461,0]);
    for (const x of [-.38,.38]) art.box(mesh,0xe6b078,[.07,.88,.03],[x,0,.46]);
    art.label(group,String(i+1),.45,.23,[startX+i*spacing,.025,.85],"#29405e","#ffffff").mesh.rotation.x=-Math.PI/2;
    const label = createValueLabel();
    label.setValue("?");
    label.sprite.position.set(0, .06, .49);
    mesh.add(label.sprite);
    labels.push(label);
    const badge=art.label(mesh,"✓",.23,.15,[0,-.31,.48],"#2b6b50","#ffffff").mesh;
    badge.visible=false; badges.push(badge);
  }

  function getBoxWorldX(index: number): number {
    return startX + index * spacing;
  }

  function getNearestIndex(x: number): number {
    let nearest = 0;
    let smallestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < count; i += 1) {
      const distance = Math.abs(getBoxWorldX(i) - x);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        nearest = i;
      }
    }
    return nearest;
  }

  function applyMaterial(index: number): void {
    const mesh = meshes[index];
    if (!mesh) return;
    if (states[index] === "found") {
      mesh.material = foundMaterial;
      return;
    }
    if (states[index] === "checked") {
      mesh.material = checkedMaterial;
      return;
    }
    if (states[index] === "eliminated") {
      mesh.material = eliminatedMaterial;
      return;
    }
    mesh.material = nearestIndex === index ? nearestMaterial : hiddenMaterial;
  }

  function setNearestIndex(index: number | null): void {
    const previous = nearestIndex;
    nearestIndex = index;
    if (previous !== null) applyMaterial(previous);
    if (index !== null) applyMaterial(index);
  }

  function setBoxState(index: number, state: BoxVisualState, value?: number): void {
    if (!meshes[index]) return;
    if(states[index]!==state&&(state==="checked"||state==="found"))pulses[index]=1;
    states[index] = state;
    if(badges[index]) badges[index]!.visible=state==="checked" || state==="found";
    if (value !== undefined) {
      labels[index]?.setValue(value);
    }
    applyMaterial(index);
  }

  function dispose(): void {
    ringGeometry.dispose();ringMaterial.dispose();
    art.dispose();
    geometry.dispose();
    hiddenMaterial.dispose();
    nearestMaterial.dispose();
    checkedMaterial.dispose();
    foundMaterial.dispose();
    eliminatedMaterial.dispose();
    labels.forEach((label) => label.dispose());
  }

  const bounds = { minX: startX, maxX: startX + (count - 1) * spacing };

  function update(dt:number) {
    elapsed+=dt;
    meshes.forEach((mesh,i)=>{
      pulses[i]=Math.max(0,pulses[i]!-dt*1.8);
      const pulse=Math.sin(pulses[i]!*Math.PI);
      const found=states[i]==='found';
      mesh.position.y=BOX_SIZE/2+pulse*.17+(found?Math.sin(elapsed*3)*.025:0);
      mesh.scale.set(1-pulse*.035,1+pulse*.09,1-pulse*.035);
      mesh.rotation.y=pulse*.07;
      rings[i]!.visible=found;rings[i]!.scale.setScalar(1+Math.sin(elapsed*4)*.06);
      labels[i]!.sprite.scale.setScalar(.6*(1+pulse*.2));
    });
  }
  return { group, bounds, update, getNearestIndex, getBoxWorldX, setNearestIndex, setBoxState, dispose };
}
